import type * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'

import type { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /* eslint-disable sort-keys */
    /**
     * Retrieves the desired check-in
     */
    checkIfEligibleForDailyCheckIn: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<boolean>(),
      reducers: {
        success: ({ state, data: { response } }): State => ({
          ...state,
          eligibleForDailyCheckIn: response,
        }),
      },
    }),
    /**
     * Initiates the check-in process
     */
    startCheckIn: store.actions.standalone({
      data:
        (
          sourcePosition: ReturnType<
            NonNullable<State['checkInSourcePosition']>
          >,
        ) =>
        () =>
          sourcePosition,
      reducer: ({ state, data }): State => ({
        ...state,
        checkInSourcePosition: data,
        checkInStage: state.checkInStage ?? 'current-mood',
      }),
    }),
    /**
     * Cancels the check-in process
     */
    stopCheckIn: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }): State => ({
        ...state,
        checkInSourcePosition: undefined,
        checkInStage: undefined,
        ignoreCheckInPrompt: false,
      }),
    }),
    /**
     * Provides an initial reaction and transitions to the full check-in
     *
     * @see {@link State.checkInCurrentMood}
     */
    giveCurrentMood: store.actions.standalone({
      data: store.actions.Data.asIs<State['checkInCurrentMood']>(),
      reducer: ({ state, data }): State => ({
        ...state,
        checkInCurrentMood: data,
        checkInStage: 'full-check-in',
      }),
    }),
    /**
     * Retrieves a new check-in
     */
    loadCheckIn: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<ents.core.summit.CheckIn | undefined>(),
      reducers: {
        request: ({ state }): State => ({
          ...state,
          checkInError: undefined,
          checkInIsLoading: true,
        }),
        success: ({ state, data: { response } }) => ({
          ...state,
          checkIn: response,
          checkInError: undefined,
          checkInIsLoading: false,
        }),
        cancellation: ({ state }): State => ({
          ...state,
          checkInError: undefined,
          checkInIsLoading: false,
        }),
        error: ({ state, data: { error } }): State => ({
          ...state,
          checkInError: error,
          checkInIsLoading: false,
        }),
      },
    }),
    /**
     * Toggles {@link State.ignoreCheckInPrompt}
     */
    setIgnoreCheckInPrompt: store.actions.standalone({
      data: (ignoreCheckInPrompt: boolean | 'toggle' = true) =>
        ignoreCheckInPrompt,
      reducer: ({ state, data }): State => ({
        ...state,
        ignoreCheckInPrompt:
          data === 'toggle' ? !state.ignoreCheckInPrompt : data,
      }),
    }),
    /**
     * Updates the in-progress response before it is submitted
     */
    setResponse: store.actions.standalone({
      data: store.actions.Data.asIs<string | undefined>(),
      reducer: ({ state, data }): State => ({
        ...state,
        response: data,
      }),
    }),
    /**
     * Submits a response to the check-in
     */
    submitResponse: store.actions.request({
      input: store.actions.Data.NONE,
      output: (
        result: Partial<Required<Pick<State, 'responseToTheResponse'>>> = {},
      ) => result,
      reducers: {
        request: ({ state }): State => ({
          ...state,
          responseToTheResponseError: undefined,
          responseToTheResponseIsLoading: true,
        }),
        success: ({
          state,
          data: {
            response: { responseToTheResponse },
          },
        }): State => ({
          ...state,
          checkIn: undefined,
          ignoreCheckInPrompt: false,
          // Remains open if there is a response to the response
          checkInStage: responseToTheResponse ? state.checkInStage : undefined,
          response: undefined,
          responseToTheResponse,
          responseToTheResponseError: undefined,
          responseToTheResponseIsLoading: false,
        }),
        cancellation: ({ state }): State => ({
          ...state,
          responseToTheResponseError: undefined,
          responseToTheResponseIsLoading: false,
        }),
        error: ({ state, data: { error } }): State => ({
          ...state,
          responseToTheResponseError: error,
          responseToTheResponseIsLoading: false,
        }),
      },
    }),
    /**
     * Finished with the entire check-in process
     */
    acknowledgeResponseToTheResponse: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }): State => ({
        ...state,
        checkInStage: undefined,
        responseToTheResponse: undefined,
      }),
    }),
    /**
     * Indicates that the user is attempting to view the {@link State.feed}
     */
    viewFeed: store.actions.standalone({
      data: store.actions.Data.NONE,
    }),
    /**
     * Retrieves the {@link State.feed}
     */
    loadFeed: store.actions.request({
      input: store.actions.Data.NONE,
      output:
        store.actions.Data.asIs<readonly ents.core.summit.CheckIn.Response[]>(),
      reducers: {
        request: ({ state }): State => ({
          ...state,
          feedError: undefined,
          feedIsLoading: true,
        }),
        success: ({ state, data: { response } }): State => ({
          ...state,
          feed: response,
          feedError: undefined,
          feedIsLoading: false,
        }),
        cancellation: ({ state }): State => ({
          ...state,
          feedIsLoading: false,
        }),
        error: ({ state, data: { error } }): State => ({
          ...state,
          feedError: error,
          feedIsLoading: false,
        }),
      },
    }),
    /**
     * Indicates that the current {@link State.feed} is no longer relevant
     *
     * Mostly useful when the context (e.g. zone) changes
     */
    clearFeed: store.actions.standalone({
      data: store.actions.Data.NONE,
      reducer: ({ state }): State => ({ ...state, feed: [] }),
    }),
    /**
     * Sets {@link State.feedSearchQuery}
     */
    setFeedSearchQuery: store.actions.standalone({
      data: store.actions.Data.asIs<string | undefined>(),
      reducer: ({ state, data }): State => ({
        ...state,
        feedSearchQuery: data,
      }),
    }),
    /* eslint-enable sort-keys */
  } as const satisfies store.actions.Config.Constraint<State>
}
